import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from 'react'
import baseCharts from '../charts'
import useSafeCustomCharts from '../hooks/useSafeCustomCharts'
import useChartColorSync from '../hooks/useChartColorSync'
import useProjectIO from '../hooks/useProjectIO'
import usePrevious from '../hooks/usePrevious'
import { DEFAULT_BRAND_THEME } from '../services/brandTheme'
import { applySafeFixes } from '../services/dataCleaning'
import { getAxisDisplayControls } from '../services/axisDisplayFormat'
import { getDefaultChartVisualOptions } from '../services/chartSnapshot'
import {
  applyBrandProfileToTheme,
  getStoredBrandProfiles,
  removeBrandProfile,
  saveStoredBrandProfiles,
  upsertBrandProfile,
} from '../services/brandProfiles'
import { useDataContext } from './DataContext'

const ChartContext = createContext(null)

export function ChartProvider({ children }) {
  const {
    data,
    userData,
    userInput,
    userDataType,
    parseError,
    unstackedData,
    unstackedColumns,
    separator,
    thousandsSeparator,
    decimalsSeparator,
    locale,
    stackDimension,
    dataSource,
    hydrateFromSavedProject,
    dataLoader,
  } = useDataContext()

  const [
    customCharts,
    {
      toConfirmCustomChart,
      confirmCustomChartLoad,
      abortCustomChartLoad,
      importCustomChartFromProject,
      exportCustomChart,
    },
  ] = useSafeCustomCharts()

  const charts = useMemo(() => baseCharts.concat(customCharts), [customCharts])

  const getInitialVisualOptions = useCallback(
    (chart) => getDefaultChartVisualOptions(chart),
    []
  )

  const [currentChart, setCurrentChart] = useState(charts[0])
  const [mapping, setMapping] = useState({})
  const [visualOptions, setVisualOptions] = useState(() =>
    getInitialVisualOptions(charts[0])
  )
  const [rawViz, setRawViz] = useState(null)
  const [mappingLoading, setMappingLoading] = useState(false)
  const [brandTheme, setBrandTheme] = useState(DEFAULT_BRAND_THEME)
  const [savedBrandProfiles, setSavedBrandProfiles] = useState(() =>
    getStoredBrandProfiles()
  )
  const [activeRecommendation, setActiveRecommendation] = useState(null)

  const dataMappingRef = useRef(null)
  const pendingRestoreMappingRef = useRef(null)

  const clearLocalMapping = useCallback(() => {
    if (dataMappingRef.current) {
      dataMappingRef.current.clearLocalMapping()
    }
  }, [])

  // The mapping workspace keeps local drag state for interaction. Project and
  // template imports must restore that state after their async data hydration
  // completes, otherwise the global chart mapping and visible Variables panel
  // can disagree.
  const applyLocalMapping = useCallback((nextMapping) => {
    if (dataMappingRef.current) {
      dataMappingRef.current.applyMapping(nextMapping)
    }
  }, [])

  const {
    chartColorSyncMode,
    setChartColorSyncMode,
    lastAppliedBrandPaletteSignature,
    setLastAppliedBrandPaletteSignature,
    chartPaletteDirty,
    applyPaletteToChartColors,
    useThemePaletteForChartColors,
    dismissBrandPaletteUpdatePrompt,
    chartColorOptionMeta,
    paletteApplyDisabledReason,
    resetChartColorSync,
  } = useChartColorSync({
    currentChart,
    data,
    mapping,
    visualOptions,
    setVisualOptions,
    brandTheme,
  })

  const {
    importProject,
    exportProject,
    exportLugsailProject,
    handleChartChange,
  } = useProjectIO({
    currentChart,
    customCharts,
    importCustomChartFromProject,
    exportCustomChart,
    hydrateFromSavedProject,
    setCurrentChart,
    brandTheme,
    setBrandTheme,
    mapping,
    setMapping,
    visualOptions,
    setVisualOptions,
    setChartColorSyncMode,
    data,
    userInput,
    userData,
    userDataType,
    parseError,
    unstackedData,
    unstackedColumns,
    separator,
    thousandsSeparator,
    decimalsSeparator,
    locale,
    stackDimension,
    dataSource,
    clearLocalMapping,
    applyLocalMapping,
    resetChartColorSync,
    setRawViz,
    pendingRestoreMappingRef,
  })

  const columnNames = useMemo(() => {
    if (data?.dataTypes) {
      return Object.keys(data.dataTypes)
    }
  }, [data])

  const prevColumnNames = usePrevious(columnNames)

  useEffect(() => {
    if (prevColumnNames) {
      const pendingMapping = pendingRestoreMappingRef.current
      if (pendingMapping !== null) {
        pendingRestoreMappingRef.current = null
        setMapping(pendingMapping)
        return
      }
      if (!columnNames) {
        setMapping({})
        clearLocalMapping()
      } else {
        const prevCols = prevColumnNames.join('.')
        const currentCols = columnNames.join('.')
        if (prevCols !== currentCols) {
          setMapping({})
          clearLocalMapping()
        }
      }
    }
  }, [columnNames, prevColumnNames, clearLocalMapping])

  const applyBrandProfile = useCallback((profile) => {
    setBrandTheme((currentTheme) => applyBrandProfileToTheme(profile, currentTheme))
  }, [])

  const saveBrandProfile = useCallback((profile) => {
    setSavedBrandProfiles((currentProfiles) => {
      const nextProfiles = upsertBrandProfile(profile, currentProfiles)
      saveStoredBrandProfiles(nextProfiles)
      return nextProfiles
    })
  }, [])

  const deleteBrandProfile = useCallback((profileId) => {
    setSavedBrandProfiles((currentProfiles) => {
      const nextProfiles = removeBrandProfile(profileId, currentProfiles)
      saveStoredBrandProfiles(nextProfiles)
      return nextProfiles
    })
  }, [])

  const applyFixes = useCallback(() => {
    if (!userData) return
    dataLoader.handleInlineEdit(applySafeFixes(userData))
  }, [dataLoader, userData])

  const axisDisplayControls = useMemo(
    () => getAxisDisplayControls(mapping, data?.dataTypes),
    [mapping, data]
  )

  return (
    <ChartContext.Provider
      value={{
        charts,
        customCharts,
        currentChart,
        setCurrentChart,
        mapping,
        setMapping,
        visualOptions,
        setVisualOptions,
        rawViz,
        setRawViz,
        mappingLoading,
        setMappingLoading,
        brandTheme,
        setBrandTheme,
        savedBrandProfiles,
        applyBrandProfile,
        saveBrandProfile,
        deleteBrandProfile,
        chartColorSyncMode,
        setChartColorSyncMode,
        lastAppliedBrandPaletteSignature,
        setLastAppliedBrandPaletteSignature,
        chartPaletteDirty,
        applyPaletteToChartColors,
        useThemePaletteForChartColors,
        dismissBrandPaletteUpdatePrompt,
        chartColorOptionMeta,
        paletteApplyDisabledReason,
        dataMappingRef,
        pendingRestoreMappingRef,
        clearLocalMapping,
        importProject,
        exportProject,
        exportLugsailProject,
        handleChartChange,
        toConfirmCustomChart,
        confirmCustomChartLoad,
        abortCustomChartLoad,
        activeRecommendation,
        setActiveRecommendation,
        axisDisplayControls,
        getInitialVisualOptions,
        applyFixes,
      }}
    >
      {children}
    </ChartContext.Provider>
  )
}

export function useChartContext() {
  const ctx = useContext(ChartContext)
  if (!ctx) throw new Error('useChartContext must be used within ChartProvider')
  return ctx
}
